/**
 * Robot Model for V2 Services
 */

export type RobotStatus = 'available' | 'cleaning' | 'charging' | 'maintenance' | 'offline';

export interface RobotLocation {
  buildingId?: string;
  floor?: number;
  apartmentId?: string;
  latitude?: number;
  longitude?: number;
  lastUpdated?: Date;
}

export interface RobotData {
  id?: string;
  name: string;
  model: string;
  serialNumber: string;
  status: RobotStatus;
  batteryLevel: number;
  location?: RobotLocation;
  currentBookingId?: string;
  totalCleanings: number;
  totalHours: number;
  lastMaintenance?: Date;
  nextMaintenance?: Date;
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export class Robot {
  public id?: string;
  public name: string;
  public model: string;
  public serialNumber: string;
  public status: RobotStatus;
  public batteryLevel: number;
  public location?: RobotLocation;
  public currentBookingId?: string;
  public totalCleanings: number;
  public totalHours: number;
  public lastMaintenance?: Date;
  public nextMaintenance?: Date;
  public metadata: Record<string, any>;
  public createdAt: Date;
  public updatedAt: Date;

  constructor(data: Partial<RobotData>) {
    this.id = data.id;
    this.name = data.name || '';
    this.model = data.model || '';
    this.serialNumber = data.serialNumber || '';
    this.status = data.status || 'offline';
    this.batteryLevel = data.batteryLevel || 0;
    this.location = data.location;
    this.currentBookingId = data.currentBookingId;
    this.totalCleanings = data.totalCleanings || 0;
    this.totalHours = data.totalHours || 0;
    this.lastMaintenance = data.lastMaintenance;
    this.nextMaintenance = data.nextMaintenance;
    this.metadata = data.metadata || {};
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }

  public toFirestore(): Record<string, any> {
    return {
      name: this.name,
      model: this.model,
      serialNumber: this.serialNumber,
      status: this.status,
      batteryLevel: this.batteryLevel,
      location: this.location,
      currentBookingId: this.currentBookingId,
      totalCleanings: this.totalCleanings,
      totalHours: this.totalHours,
      lastMaintenance: this.lastMaintenance,
      nextMaintenance: this.nextMaintenance,
      metadata: this.metadata,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }

  public static fromFirestore(data: any): Robot {
    return new Robot({
      ...data,
      createdAt: data.createdAt?.toDate?.() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      lastMaintenance: data.lastMaintenance?.toDate?.() || data.lastMaintenance,
      nextMaintenance: data.nextMaintenance?.toDate?.() || data.nextMaintenance,
      location: data.location ? {
        ...data.location,
        lastUpdated: data.location.lastUpdated?.toDate?.() || data.location.lastUpdated
      } : undefined
    });
  }

  public updateStatus(status: RobotStatus): void {
    this.status = status;
    this.updatedAt = new Date();
  }

  public updateLocation(location: RobotLocation): void {
    this.location = {
      ...location,
      lastUpdated: new Date()
    };
    this.updatedAt = new Date();
  }

  public assignToBooking(bookingId: string): void {
    this.currentBookingId = bookingId;
    this.status = 'cleaning';
    this.updatedAt = new Date();
  }

  public completeBooking(): void {
    this.currentBookingId = undefined;
    this.status = 'available';
    this.totalCleanings++;
    this.updatedAt = new Date();
  }
}